/**
 * RebookTargetChoice — the "Move to" slot in the rebook drawer (BON-146):
 * either a prompt to open the full calendar, or the chosen workshop with a
 * Change control.
 *
 * The drawer deliberately does NOT inline a list of candidates. Same-price
 * targets across a region and a season is a calendar-sized problem, and a short
 * list in a drawer would quietly hide the date the customer actually asked for.
 */
import { Calendar, LayoutGrid } from 'lucide-react';

import type { PickerWorkshop } from './shared/pickerWorkshop';
import {
  formatPickerDate,
  formatPickerTime,
  formatPricePerSeat,
} from './shared/WorkshopPickerHelpers';

export interface RebookTargetChoiceProps {
  target: PickerWorkshop | null;
  onBrowse: () => void;
}

export function RebookTargetChoice({ target, onBrowse }: RebookTargetChoiceProps) {
  if (!target) {
    return (
      <button
        type="button"
        onClick={onBrowse}
        className="flex w-full items-center gap-3 rounded-lg border border-dashed border-stone-300 px-3.5 py-3.5 text-left hover:border-moss-400 dark:border-stone-600"
      >
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-moss-50 text-moss-700 dark:bg-moss-900/20 dark:text-moss-300">
          <Calendar size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-bark-900 dark:text-stone-100">
            Choose a workshop
          </span>
          <span className="block text-xs text-stone-500 dark:text-stone-400">
            Browse the calendar — same price, same region
          </span>
        </span>
        <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-moss-600 px-3 py-1.5 text-xs font-semibold text-white">
          <LayoutGrid size={13} />
          Browse
        </span>
      </button>
    );
  }

  const date = formatPickerDate(target.localStartsAt);
  const time = formatPickerTime(target.localStartsAt);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-moss-400 bg-moss-50/60 px-3.5 py-3 dark:border-moss-700 dark:bg-moss-900/10">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-bark-900 dark:text-stone-100">
          {target.typeName} &middot; {target.venueName}
        </p>
        <p className="truncate text-xs text-stone-500 dark:text-stone-400">
          {date}
          {time ? ` · ${time}` : ''}
          {target.venueCity ? ` · ${target.venueCity}` : ''} &middot; {target.seatsLeft} seats left
          &middot; {formatPricePerSeat(target.priceCents)}
        </p>
      </div>
      <button
        type="button"
        onClick={onBrowse}
        className="flex-shrink-0 rounded-lg border border-stone-300 px-2.5 py-1 text-xs font-semibold text-stone-700 hover:bg-white dark:border-stone-600 dark:text-stone-200"
      >
        Change
      </button>
    </div>
  );
}
